/**
 * Input with End Select Renderer
 *
 * Text input with a dropdown selector at the end (e.g., domain with TLD)
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Input with End Select Renderer
 * Features:
 * - Dropdown selector on the right
 * - Combined value (input + select)
 * - Perfect for domains (example.com, example.net, etc.)
 * - Auto-parsing of values
 */
export const renderInputWithEndSelect: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    isRequired
}) => {
    // Parse the combined value to separate input and select parts
    const fullValue = value || '';
    const defaultSelectValue = field.ui?.endSelectOptions?.[0]?.value || '';

    // Find which option suffix matches the current value
    let selectValue = defaultSelectValue;
    let inputValue = fullValue;

    if (fullValue && field.ui?.endSelectOptions) {
        for (const option of field.ui.endSelectOptions) {
            if (fullValue.endsWith(option.value)) {
                selectValue = option.value;
                inputValue = fullValue.substring(0, fullValue.length - option.value.length);
                break;
            }
        }
    }

    const handleSelectChange = (newSelectValue: string) => {
        // Combine the existing input value with new select value
        const combinedValue = inputValue + newSelectValue;
        handleChange(field.name, combinedValue);
    };

    const handleInputChange = (newInputValue: string) => {
        // Combine the new input value with current select value
        const combinedValue = newInputValue + selectValue;
        handleChange(field.name, combinedValue);
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="flex rounded-md shadow-xs">
                <Input
                    id={field.name}
                    type={field.inputType || 'text'}
                    placeholder={field.placeholder}
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    maxLength={field.maxLength}
                    className={`-me-px rounded-e-none shadow-none focus-visible:z-10 ${hasError ? 'border-destructive' : ''}`}
                />
                <select
                    value={selectValue}
                    onChange={(e) => handleSelectChange(e.target.value)}
                    className="w-fit rounded-s-none text-muted-foreground shadow-none hover:text-foreground border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                    {field.ui?.endSelectOptions?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default renderInputWithEndSelect;
